/**
 * Offline Safety Service
 * 
 * Caches safety-related information for offline access:
 * - Travel advisories
 * - Embassy information
 * - Emergency contacts (trusted contacts)
 * - Weather advisories
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { offlineStorage } from './offlineStorage';
import { networkMonitor } from './networkMonitor';
import type { TravelAdvisory } from './travelAdvisories';
import type { EmbassyInfo } from './embassy';
import type { TrustedContact } from './safety';
import type { WeatherAdvisory } from './weather';

const SAFETY_CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000; // 7 days
const ADVISORY_CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 1 day (advisories change more frequently)

type CachedSafetyData = {
  travelAdvisories: Record<string, TravelAdvisory[]>; // countryCode -> advisories
  embassyInfo: Record<string, EmbassyInfo>; // citizenshipCountryCode -> embassy info
  trustedContacts: TrustedContact[];
  weatherAdvisories: Record<string, WeatherAdvisory[]>; // location -> advisories
  lastSynced: number;
  expiresAt: number;
};

const SAFETY_CACHE_KEY = '@tripflow_safety_cache';
/** User explicitly downloaded Safety for offline (Profile / Safety). Not set by background caching. */
const SAFETY_OFFLINE_USER_PACK_KEY = '@tripflow_safety_offline_user_pack_v1';

/**
 * Whether the user chose to keep Safety data for offline use.
 * Legacy: if the key is unset but a cache blob exists, we migrate once to `true` so existing installs keep behavior.
 */
export async function getSafetyOfflineUserPackEnabled(): Promise<boolean> {
  try {
    const raw = await AsyncStorage.getItem(SAFETY_OFFLINE_USER_PACK_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    const lastSynced = await readSafetyLastSyncFromStorage();
    if (lastSynced != null) {
      await AsyncStorage.setItem(SAFETY_OFFLINE_USER_PACK_KEY, 'true');
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export async function setSafetyOfflineUserPackEnabled(enabled: boolean): Promise<void> {
  try {
    await AsyncStorage.setItem(SAFETY_OFFLINE_USER_PACK_KEY, enabled ? 'true' : 'false');
  } catch (error) {
    console.error('[OfflineSafety] Error saving offline pack preference:', error);
  }
}

/** Ensure offline pack has a `lastSynced` time (e.g. user downloaded with no contacts yet). */
export async function touchSafetyOfflinePackTimestamp(): Promise<void> {
  try {
    if (!(await getSafetyOfflineUserPackEnabled())) {
      return;
    }
    const cached = (await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY)) || {
      travelAdvisories: {},
      embassyInfo: {},
      trustedContacts: [],
      weatherAdvisories: {},
      lastSynced: 0,
      expiresAt: Date.now() + SAFETY_CACHE_EXPIRATION,
    };
    cached.lastSynced = Date.now();
    cached.expiresAt = Date.now() + SAFETY_CACHE_EXPIRATION;
    await offlineStorage.set(SAFETY_CACHE_KEY, cached, SAFETY_CACHE_EXPIRATION);
  } catch (error) {
    console.error('[OfflineSafety] Error touching safety pack timestamp:', error);
  }
}

/** Last sync timestamp from storage only (no extra semantics). */
async function readSafetyLastSyncFromStorage(): Promise<number | null> {
  try {
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY);
    return cached?.lastSynced || null;
  } catch {
    return null;
  }
}

/**
 * Cache travel advisories for a country
 */
export async function cacheTravelAdvisories(
  countryCode: string,
  advisories: TravelAdvisory[]
): Promise<void> {
  try {
    if (!(await getSafetyOfflineUserPackEnabled())) {
      return;
    }
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY) || {
      travelAdvisories: {},
      embassyInfo: {},
      trustedContacts: [],
      weatherAdvisories: {},
      lastSynced: 0,
      expiresAt: Date.now() + SAFETY_CACHE_EXPIRATION,
    };

    cached.travelAdvisories[countryCode.toUpperCase()] = advisories;
    cached.lastSynced = Date.now();
    cached.expiresAt = Date.now() + SAFETY_CACHE_EXPIRATION;

    await offlineStorage.set(SAFETY_CACHE_KEY, cached, SAFETY_CACHE_EXPIRATION);
    console.log(`[OfflineSafety] Cached ${advisories.length} travel advisories for ${countryCode}`);
  } catch (error) {
    console.error(`[OfflineSafety] Error caching travel advisories for ${countryCode}:`, error);
  }
}

/**
 * Get cached travel advisories for a country
 */
export async function getCachedTravelAdvisories(
  countryCode: string
): Promise<TravelAdvisory[]> {
  try {
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY);
    if (!cached || !cached.travelAdvisories) {
      return [];
    }

    // Check expiration
    if (cached.expiresAt && Date.now() > cached.expiresAt) {
      console.log('[OfflineSafety] Safety cache expired');
      return [];
    }

    return cached.travelAdvisories[countryCode.toUpperCase()] || [];
  } catch (error) {
    console.error(`[OfflineSafety] Error getting cached travel advisories for ${countryCode}:`, error);
    return [];
  }
}

/**
 * Cache embassy information
 */
export async function cacheEmbassyInfo(
  citizenshipCountryCode: string,
  embassyInfo: EmbassyInfo
): Promise<void> {
  try {
    if (!(await getSafetyOfflineUserPackEnabled())) {
      return;
    }
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY) || {
      travelAdvisories: {},
      embassyInfo: {},
      trustedContacts: [],
      weatherAdvisories: {},
      lastSynced: 0,
      expiresAt: Date.now() + SAFETY_CACHE_EXPIRATION,
    };

    cached.embassyInfo[citizenshipCountryCode.toUpperCase()] = embassyInfo;
    cached.lastSynced = Date.now();
    cached.expiresAt = Date.now() + SAFETY_CACHE_EXPIRATION;

    await offlineStorage.set(SAFETY_CACHE_KEY, cached, SAFETY_CACHE_EXPIRATION);
    console.log(`[OfflineSafety] Cached embassy info for ${citizenshipCountryCode}`);
  } catch (error) {
    console.error(`[OfflineSafety] Error caching embassy info for ${citizenshipCountryCode}:`, error);
  }
}

/**
 * Get cached embassy information
 */
export async function getCachedEmbassyInfo(
  citizenshipCountryCode: string
): Promise<EmbassyInfo | null> {
  try {
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY);
    if (!cached || !cached.embassyInfo) {
      return null;
    }

    // Check expiration
    if (cached.expiresAt && Date.now() > cached.expiresAt) {
      console.log('[OfflineSafety] Safety cache expired');
      return null;
    }

    return cached.embassyInfo[citizenshipCountryCode.toUpperCase()] || null;
  } catch (error) {
    console.error(`[OfflineSafety] Error getting cached embassy info for ${citizenshipCountryCode}:`, error);
    return null;
  }
}

/**
 * Cache trusted contacts
 */
export async function cacheTrustedContacts(
  contacts: TrustedContact[]
): Promise<void> {
  try {
    if (!(await getSafetyOfflineUserPackEnabled())) {
      return;
    }
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY) || {
      travelAdvisories: {},
      embassyInfo: {},
      trustedContacts: [],
      weatherAdvisories: {},
      lastSynced: 0,
      expiresAt: Date.now() + SAFETY_CACHE_EXPIRATION,
    };

    cached.trustedContacts = contacts;
    cached.lastSynced = Date.now();
    cached.expiresAt = Date.now() + SAFETY_CACHE_EXPIRATION;

    await offlineStorage.set(SAFETY_CACHE_KEY, cached, SAFETY_CACHE_EXPIRATION);
    console.log(`[OfflineSafety] Cached ${contacts.length} trusted contacts`);
  } catch (error) {
    console.error('[OfflineSafety] Error caching trusted contacts:', error);
  }
}

/**
 * Get cached trusted contacts
 */
export async function getCachedTrustedContacts(): Promise<TrustedContact[]> {
  try {
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY);
    if (!cached || !cached.trustedContacts) {
      return [];
    }

    // Check expiration
    if (cached.expiresAt && Date.now() > cached.expiresAt) {
      console.log('[OfflineSafety] Safety cache expired');
      return [];
    }

    return cached.trustedContacts;
  } catch (error) {
    console.error('[OfflineSafety] Error getting cached trusted contacts:', error);
    return [];
  }
}

/**
 * Cache weather advisories for a location
 */
export async function cacheWeatherAdvisories(
  location: string,
  advisories: WeatherAdvisory[]
): Promise<void> {
  try {
    if (!(await getSafetyOfflineUserPackEnabled())) {
      return;
    }
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY) || {
      travelAdvisories: {},
      embassyInfo: {},
      trustedContacts: [],
      weatherAdvisories: {},
      lastSynced: 0,
      expiresAt: Date.now() + SAFETY_CACHE_EXPIRATION,
    };

    cached.weatherAdvisories[location] = advisories;
    cached.lastSynced = Date.now();
    cached.expiresAt = Date.now() + ADVISORY_CACHE_EXPIRATION; // Weather advisories expire faster

    await offlineStorage.set(SAFETY_CACHE_KEY, cached, SAFETY_CACHE_EXPIRATION);
    console.log(`[OfflineSafety] Cached ${advisories.length} weather advisories for ${location}`);
  } catch (error) {
    console.error(`[OfflineSafety] Error caching weather advisories for ${location}:`, error);
  }
}

/**
 * Get cached weather advisories for a location
 */
export async function getCachedWeatherAdvisories(
  location: string
): Promise<WeatherAdvisory[]> {
  try {
    const cached = await offlineStorage.get<CachedSafetyData>(SAFETY_CACHE_KEY);
    if (!cached || !cached.weatherAdvisories) {
      return [];
    }

    // Check expiration
    if (cached.expiresAt && Date.now() > cached.expiresAt) {
      console.log('[OfflineSafety] Safety cache expired');
      return [];
    }

    return cached.weatherAdvisories[location] || [];
  } catch (error) {
    console.error(`[OfflineSafety] Error getting cached weather advisories for ${location}:`, error);
    return [];
  }
}

/**
 * Get last sync time
 */
export async function getSafetyLastSyncTime(): Promise<number | null> {
  try {
    return await readSafetyLastSyncFromStorage();
  } catch (error) {
    console.error('[OfflineSafety] Error getting last sync time:', error);
    return null;
  }
}

/** UI: show Safety as downloaded only when the user opted in and cache is still present. */
export async function getSafetyOfflineUiState(): Promise<{
  lastSynced: number | null;
  showAsDownloaded: boolean;
}> {
  const optedIn = await getSafetyOfflineUserPackEnabled();
  if (!optedIn) {
    return { lastSynced: null, showAsDownloaded: false };
  }
  const lastSynced = await getSafetyLastSyncTime();
  return {
    lastSynced,
    showAsDownloaded: lastSynced != null,
  };
}

/**
 * Clear all cached safety data and offline-pack preference (e.g. on sign-out).
 */
export async function clearSafetyOfflineSessionData(): Promise<void> {
  try {
    await offlineStorage.remove(SAFETY_CACHE_KEY);
    await AsyncStorage.removeItem(SAFETY_OFFLINE_USER_PACK_KEY);
  } catch (error) {
    console.error('[OfflineSafety] Error clearing safety session data:', error);
  }
}

/**
 * Clear all cached safety data
 */
export async function clearSafetyCache(): Promise<void> {
  try {
    await offlineStorage.remove(SAFETY_CACHE_KEY);
    await setSafetyOfflineUserPackEnabled(false);
    console.log('[OfflineSafety] Cleared all safety cache');
  } catch (error) {
    console.error('[OfflineSafety] Error clearing safety cache:', error);
  }
}

/**
 * Auto-cache safety data for active trips
 * This should be called when trips are loaded and user is online
 */
export async function autoCacheSafetyDataForTrips(
  trips: Array<{ destinations?: Array<{ country?: string }>; destination?: string }>,
  citizenshipCountryCode?: string
): Promise<void> {
  if (!networkMonitor.isOnline()) {
    return; // Don't cache when offline
  }

  try {
    // Extract unique country codes from trips
    const countryCodes = new Set<string>();
    trips.forEach(trip => {
      if (trip.destinations && trip.destinations.length > 0) {
        trip.destinations.forEach(dest => {
          if (dest.country) {
            countryCodes.add(dest.country);
          }
        });
      } else if (trip.destination) {
        countryCodes.add(trip.destination);
      }
    });

    console.log(`[OfflineSafety] Auto-caching safety data for ${countryCodes.size} countries`);
    
    // Note: The actual fetching and caching should be done by the calling code
    // This function just provides the interface for auto-caching
    // The safety screen should call this and then fetch/cache the data
  } catch (error) {
    console.error('[OfflineSafety] Error auto-caching safety data:', error);
  }
}

